/**
 * Firebase Authentication Service
 * Handles all authentication related operations
 */

import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

/**
 * Register a new user with email and password
 * 
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<UserCredential>} User credential object
 */
export const registerWithEmailAndPassword = (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Sign in with email and password
 * 
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<UserCredential>} User credential object
 */
export const loginWithEmailAndPassword = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

/**
 * Send password reset email
 * 
 * @param {string} email - User's email
 * @returns {Promise<void>}
 */
export const resetPassword = (email) => {
  return sendPasswordResetEmail(auth, email);
};

/**
 * Sign out current user
 * 
 * @returns {Promise<void>}
 */
export const logout = () => {
  return signOut(auth);
};

/**
 * Update user profile (display name and photo URL)
 * 
 * @param {Object} user - Firebase user object
 * @param {Object} data - Profile data to update
 * @param {string} data.displayName - User's display name
 * @param {string} data.photoURL - User's photo URL
 * @returns {Promise<void>}
 */
export const updateUserProfile = (user, { displayName, photoURL }) => {
  return updateProfile(user, { displayName, photoURL });
};

/**
 * Update user email
 * 
 * @param {Object} user - Firebase user object
 * @param {string} newEmail - New email address
 * @returns {Promise<void>}
 */
export const updateUserEmail = (user, newEmail) => {
  return updateEmail(user, newEmail);
};

/**
 * Send OTP to email
 * 
 * @param {string} email - User's email
 * @returns {Promise<any>} Result from cloud function
 */
export const sendOTP = (email) => {
  const sendOTPFunction = httpsCallable(functions, 'sendOTP');
  return sendOTPFunction({ email });
};

/**
 * Verify OTP code
 * 
 * @param {string} email - User's email
 * @param {string} otp - OTP code to verify
 * @returns {Promise<any>} Result from cloud function
 */
export const verifyOTP = (email, otp) => {
  const verifyOTPFunction = httpsCallable(functions, 'verifyOTP');
  return verifyOTPFunction({ email, otp });
};

/**
 * Get current authenticated user
 * 
 * @returns {Object|null} Firebase user object or null if not authenticated
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};